import { ArrowDown16 } from '@honeybook/icons';
import styled, { css } from 'styled-components';
import { flexCenterAll } from '../../styles/mixins';
import { TargetContainer } from '../HBPopover/HBPopover.styles';
import { Button, buttonThemes, Props as ButtonProps } from './HBButton.styles';
import type { TScopeSizeMap } from './HBButton.types';
import HBClickable from '../HBClickable/HBClickable';
import { vars } from '../../theme';

interface SplitButtonProps extends ButtonProps {
    isSplitMenuOpen?: boolean;
    isSplitMenuTop?: boolean;
    isMainButtonDisabled?: boolean;
    isMenuButtonDisabled?: boolean;
}

/**
 * Gets the border style for SplitButton.
 * Uses real CSS border instead of inset box-shadow so that:
 * 1. Child backgrounds don't cover the border on hover
 * 2. Custom padding/gap on the button doesn't break the border
 */
const getSplitButtonBorder = (theme: string, isDisabled?: boolean): string => {
    const buttonTheme = buttonThemes[theme];
    if (!buttonTheme) {
        return 'none';
    }

    const { boxShadow, splitterBackground } = buttonTheme;

    // Only apply border for themes that use inset box-shadow as border
    if (!boxShadow || boxShadow === 'none' || !boxShadow.includes('inset')) {
        return 'none';
    }

    const color = isDisabled ? vars.colors.stroke.disabled : splitterBackground;
    if (!color) {
        return 'none';
    }

    return `1px solid ${color}`;
};

const splitChildrenStyle = (isDisabled: boolean) => css`
    display: flex;
    height: 100%;
    align-items: center;
    transition: inherit;
    cursor: pointer;
    ${!isDisabled &&
    css`
        &:hover {
            background: ${({ theme }) => buttonThemes[theme].hoverBackground};
        }
    `}
`;

const splitButtonPaddings: TScopeSizeMap<{ text: number; target: number }> = {
    system: {
        xs: {
            text: 10,
            target: 6
        },

        sm: {
            text: 12,
            target: 8
        },

        md: {
            text: 14,
            target: 10
        },

        lg: {
            text: 20,
            target: 14
        }
    },
    builder: {
        sm: {
            text: 12,
            target: 8
        },

        md: {
            text: 14,
            target: 10
        },

        xl: {
            text: 20,
            target: 14
        }
    }
} as const;

const splitChildrenDisabledStyle = css`
    pointer-events: none;
    opacity: 0.3;
    cursor: default;
`;

export const SplitButtonArrowIcon = styled(ArrowDown16)``;

export const SplitTextContainer = styled.div`
    height: 100%;
    ${flexCenterAll}
`;

export const SplitButton = styled(Button)<SplitButtonProps>`
    padding: 0;
    cursor: default;

    /*
     * For themes using inset box-shadow as border, use real CSS border instead.
     * This ensures child backgrounds don't cover the border on hover,
     * and custom padding/gap from consumers doesn't break the border.
     */
    &&& {
        box-shadow: ${({ theme }) => {
            const { boxShadow } = buttonThemes[theme] || {};
            if (boxShadow && boxShadow.includes('inset')) {
                return 'none';
            }
            return boxShadow;
        }};
        border: ${({ theme, disabled }) =>
            getSplitButtonBorder(theme, disabled)};
    }

    &&:hover {
        background: ${({ theme }) => buttonThemes[theme].background} !important;
    }
    ${TargetContainer}:first-of-type {
        height: 100%;
        transition: inherit;
        border-top-left-radius: inherit;
        border-bottom-left-radius: inherit;
        ${SplitTextContainer} {
            ${({ isMainButtonDisabled }) =>
                splitChildrenStyle(isMainButtonDisabled)}
            border-top-left-radius: inherit;
            border-bottom-left-radius: inherit;
            padding: 1px
                ${({ scope, size }) => splitButtonPaddings[scope][size].text}px
                0;
        }
        &:hover + ${TargetContainer}:last-of-type::before {
            ${({ isMainButtonDisabled }) =>
                !isMainButtonDisabled &&
                css`
                    opacity: 1;
                `}
        }
    }
    ${TargetContainer}:last-of-type {
        ${({ isMenuButtonDisabled }) =>
            splitChildrenStyle(isMenuButtonDisabled)}
        border-top-right-radius: inherit;
        border-bottom-right-radius: inherit;
        padding: 1px
            ${({ scope, size }) => splitButtonPaddings[scope][size].target}px 0;
        position: relative;
        &::before {
            left: 0;
            top: 0;
            position: absolute;
            content: '';
            height: 100%;
            width: 1px;
            transition: inherit;
            transition-property: opacity, background;
            background: ${({ theme }) =>
                buttonThemes[theme].splitterBackground};
            opacity: 0.7;
            ${({ disabled }) =>
                disabled &&
                css`
                    background: ${vars.colors.stroke.disabled};
                    opacity: 1;
                `}
            ${({ isSplitMenuOpen }) =>
                isSplitMenuOpen &&
                css`
                    opacity: 1;
                `}
        }
        &:hover::before {
            opacity: 1;
        }
        ${({ isMenuButtonDisabled }) =>
            isMenuButtonDisabled &&
            css`
                ${splitChildrenDisabledStyle}
            `}
        ${({ isSplitMenuOpen, theme }) =>
            isSplitMenuOpen &&
            css`
                background: ${buttonThemes[theme].hoverBackground};
            `}
        ${({ isSplitMenuOpen, isSplitMenuTop }) =>
            isSplitMenuOpen &&
            isSplitMenuTop &&
            css`
                ${SplitButtonArrowIcon} {
                    transform: rotate(180deg);
                }
            `}
    }

    i {
        display: flex;
    }
`;

export const MenuWrapper = styled(HBClickable)`
    height: 100%;
    border-top-right-radius: inherit;
    transition: inherit;
    border-bottom-right-radius: inherit;
`;
